import { createContext, useContext, useEffect, useState } from 'react';
import { get, User } from '@/api/auth/auth';
import { useMsal } from '@azure/msal-react';

export type Route = {
	title: string;
	url: string;
	icon?: React.ReactNode;
	children?: Route[];
};

const AuthContext = createContext<{
	user: User | null;
	nav: Route[];
	routes: string[] | null;
}>({
	user: null,
	nav: [],
	routes: null,
});

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
	const { accounts } = useMsal();
	const [user, setUser] = useState<User | null>(null);
	const [nav, setNav] = useState<Route[]>([]);
	const [routes, setRoutes] = useState<string[] | null>(null);

	useEffect(() => {
		if (accounts.length > 0) {
			// Get the active session from db
			get().then((user) => {
				setUser(user);
				const tempNav: Route[] = [];
				tempNav.push({
					title: 'Submission Scheduler',
					url: '/submission-scheduler',
				});
				if (user.geoveraAccess) {
					tempNav.push({
						title: 'Geovera',
						url: '/geovera/checks',
						children: [
							{
								title: 'Checks',
								url: '/geovera/checks',
							},
							{
								title: 'Policies',
								url: '/geovera/policies',
							},
						],
					});
				}
				if (user.wellsFargoAccess) {
					tempNav.push({
						title: 'Wells Fargo',
						url: '/wells-fargo',
					});
				}
				if (user.speedbuilderAccess) {
					tempNav.push({
						title: 'Speedbuilder',
						url: '/speedbuilder',
					});
				}
				if (user.northlightAccess) {
					tempNav.push({
						title: 'Northlight',
						url: '/northlight',
					});
				}
				if (user.northlightRenewalAccess) {
					tempNav.push({
						title: 'Northlight Renewal',
						url: '/northlight-renewal',
					});
				}
				if (user.commissionStatementAccess) {
					tempNav.push({
						title: 'Commission Statement',
						url: '/commission-statement',
					});
				}
				if (user.dbStatementUploadAccess) {
					tempNav.push({
						title: 'DB Statement Upload',
						url: '/db-statement-upload',
					});
				}
				if (user.directBillAccess) {
					tempNav.push({
						title: 'Direct Bill',
						url: '/direct-bill',
					});
				}
				if (user.blacklineAccess) {
					tempNav.push({
						title: 'Blackline',
						url: '/blackline',
					});
				}
				if (user.blacklineBillingAccess) {
					tempNav.push({
						title: 'Blackline Billing Audit',
						url: '/blackline-billing-audit',
					});
				}
				if (user.fireballAdminAccess) {
					tempNav.push({
						title: 'Admin',
						url: '/admin',
					});
					tempNav.push({
						title: 'Workday',
						url: '/workday',
					});
					tempNav.push({
						title: 'Doc',
						url: '/doc/classes',
						children: [
							{
								title: 'Data Classifications',
								url: '/doc/data-classifications',
							},
							{
								title: 'Document Classes',
								url: '/doc/document-classes',
							},
							{
								title: 'Document Types',
								url: '/doc/document-types',
							},
							{
								title: 'Entity Types',
								url: '/doc/entity-types',
							},
							{
								title: 'Metadata Types',
								url: '/doc/metadata-types',
							},
							{
								title: 'Source Systems',
								url: '/doc/source-systems',
							},
						],
					});
					tempNav.push({
						title: 'Fees',
						url: '/fee/charges',
						children: [
							{
								title: 'Charges',
								url: '/fee/charges',
							},
							{
								title: 'State Mappings',
								url: '/fee/state-mappings',
							},
						],
					});
					tempNav.push({
						title: 'Policy Status',
						url: '/policy-status',
					});
				}
				if (user.dex360AuditAccess) {
					tempNav.push({
						title: 'Dex360 Audit',
						url: '/dex360-audit',
					});
				}
				if (user.accountingAccess) {
					tempNav.push({
						title: 'Velocity Feed Audit',
						url: '/velocity-feed-audit',
					});
				}

				const tempRoutes = tempNav.flatMap((nav) => nav.children ?? nav);
				const tempRoutesString = tempRoutes.map((route) => route.url);
				setNav(tempNav);
				setRoutes(tempRoutesString);
			});
		}
	}, [accounts]);

	return (
		<AuthContext.Provider value={{ user, nav, routes }}>
			{children}
		</AuthContext.Provider>
	);
};

export const useAuth = () => {
	return useContext(AuthContext);
};
